<template>
  <div class="page-features">
    <landing-header />
    <main>

      <!-- ===== HERO ===== -->
      <section class="hero-section">
        <div class="max-w-6xl mx-auto px-4 text-center">
          <span class="hero-badge">Plateforme CVC tout-en-un</span>
          <h1 class="hero-title">
            Faites tourner toute votre société<br/>
            <span class="gradient-text">avec un seul outil</span>
          </h1>
          <p class="hero-sub">
            Du technicien sur le terrain au gérant derrière son bureau — Termoflow couvre chaque aspect de votre métier. Sans double saisie, sans outil externe, sans surprise sur la facture.
          </p>
          <div class="hero-proof">
            <div class="hp-item"><span class="hp-val">20+</span><span class="hp-lab">fonctionnalités</span></div>
            <div class="hp-sep"></div>
            <div class="hp-item"><span class="hp-val">1</span><span class="hp-lab">seul outil</span></div>
            <div class="hp-sep"></div>
            <div class="hp-item"><span class="hp-val">0 €</span><span class="hp-lab">de surcoût</span></div>
            <div class="hp-sep"></div>
            <div class="hp-item"><span class="hp-val">100%</span><span class="hp-lab">conforme TVA</span></div>
          </div>
        </div>
      </section>

      <!-- ===== GROUPES ===== -->
      <section class="groups-section">
        <div class="max-w-6xl mx-auto px-4">

          <div v-for="group in featureGroups" :key="group.id" class="group-block" :id="group.id">
            <div class="group-header">
              <div class="group-icon-wrap" :class="group.color">
                <i :class="group.icon" aria-hidden="true"></i>
              </div>
              <div>
                <h2 class="group-title">{{ group.title }}</h2>
                <p class="group-desc">{{ group.description }}</p>
              </div>
            </div>

            <div class="feat-grid" :class="group.features.length === 4 ? 'cols-2' : 'cols-' + Math.min(group.features.length, 3)">
              <a v-for="feat in group.features" :key="feat.name" :href="feat.href" class="feat-card" :class="feat.highlight ? 'feat-highlight' : ''">
                <div class="fc-top">
                  <div class="fc-icon" :class="group.color">
                    <i :class="feat.icon" aria-hidden="true"></i>
                  </div>
                  <span v-if="feat.badge" class="fc-badge"
                    :class="feat.badge === 'Inclus' ? 'badge-green' : feat.badge === 'Nouveau' ? 'badge-orange' : feat.badge === '2026 ready' ? 'badge-blue' : 'badge-gray'">
                    {{ feat.badge }}
                  </span>
                </div>
                <h3 class="fc-name">{{ feat.name }}</h3>
                <p class="fc-desc">{{ feat.description }}</p>
                <ul class="fc-pills">
                  <li v-for="p in feat.pills" :key="p">{{ p }}</li>
                </ul>
                <span class="fc-link">En savoir plus <i class="fa-solid fa-arrow-right" aria-hidden="true"></i></span>
              </a>
            </div>
          </div>

        </div>
      </section>

      <!-- ===== TABLEAU COMPARATIF ===== -->
      <section class="compare-section">
        <div class="max-w-5xl mx-auto px-4">
          <div class="text-center mb-14">
            <h2 class="text-3xl font-black text-white mb-4">Termoflow remplace combien d'outils ?</h2>
            <p class="text-slate-400">Ce que les entreprises CVC jonglaient avant — et ce qu'ils ont supprimé.</p>
          </div>
          <div class="compare-grid">
            <div class="compare-col before-col">
              <div class="compare-col-head before-head">Avant Termoflow</div>
              <div class="compare-item" v-for="item in beforeItems" :key="item.tool">
                <i class="fa-solid fa-box" aria-hidden="true"></i>
                <div><b>{{ item.tool }}</b><small>{{ item.role }}</small></div>
                <span class="before-cost">{{ item.cost }}</span>
              </div>
              <div class="before-total">
                <span>Total estimé</span><span class="bt-val">~130 €/mois</span>
              </div>
            </div>
            <div class="compare-arrow">
              <i class="fa-solid fa-right-long"></i>
              <span>Remplacé par</span>
            </div>
            <div class="compare-col after-col">
              <div class="compare-col-head after-head">Avec Termoflow</div>
              <div class="after-logo"><i class="fa-solid fa-fire-burner"></i> Termoflow</div>
              <div class="after-checks">
                <div v-for="c in afterChecks" :key="c" class="after-check">
                  <i class="fa-solid fa-check" aria-hidden="true"></i>{{ c }}
                </div>
              </div>
              <div class="after-total">
                <span>Un seul abonnement</span>
                <span class="at-sub">Tout inclus, zéro surprise</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="cta-section">
        <div class="max-w-3xl mx-auto px-4 text-center">
          <h2 class="text-4xl font-black text-gray-900 mb-6">Prêt à simplifier votre quotidien ?</h2>
          <p class="text-xl text-gray-500 mb-10">Rejoignez les entreprises CVC qui ont choisi Termoflow pour piloter leur activité avec sérénité.</p>
          <div class="cta-buttons">
            <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
            <a href="/tarifs" class="btn-outline-large">Voir les tarifs</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>
    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'FeaturesPage',
  components: { LandingHeader, LandingFooter, LandingAvis, 'landing-roi': LandingROI },
  data() {
    return {
      beforeItems: [
        { tool: 'Logiciel de facturation', role: 'Devis, factures, relances', cost: '~25 €/mois' },
        { tool: 'Agenda & planning', role: 'Google Cal, Calendly...', cost: '~10 €/mois' },
        { tool: 'Application terrain', role: 'Rapports & bons d\'intervention', cost: '~20 €/mois' },
        { tool: 'Fichier clients', role: 'Excel, Notion, Trello...', cost: '~10 €/mois' },
        { tool: 'Prise de RDV en ligne', role: 'Calendly, SimplyBook...', cost: '~15 €/mois' },
        { tool: 'Registre fluides F-Gaz', role: 'CERFA & traçabilité', cost: '~20 €/mois' },
        { tool: 'SMS & emails auto', role: 'Rappels & confirmations', cost: '~15 €/mois' },
        { tool: 'Portail client', role: 'Documents, historique', cost: '~15 €/mois' },
      ],
      afterChecks: [
        'Facturation complète & certifiée anti-fraude TVA',
        'Planning multi-techniciens par demi-journée',
        'Suivi des chantiers & interventions',
        'Prise de RDV en ligne personnalisable',
        'CRM clients, régies, locataires',
        'Stock camion & dépôt en temps réel',
        'CERFA fluides générés automatiquement',
        'App mobile iOS & Android (offline)',
        'Portail client libre-service',
        'SMS et emails automatisés',
        'Statistiques & pilotage',
        'Paramétrage métier complet',
      ],
      featureGroups: [
        {
          id: 'terrain',
          icon: 'fa-solid fa-helmet-safety',
          color: 'orange',
          title: 'Sur le terrain',
          description: 'Les outils que vos techniciens utilisent chaque jour — conçus pour aller vite, même les mains dans le cambouis.',
          features: [
            {
              name: 'Interventions',
              href: '/interventions',
              icon: 'fa-solid fa-screwdriver-wrench',
              badge: null,
              description: 'Suivez chaque intervention en temps réel. Statuts, rapport photo, signature client, historique complet par appareil.',
              pills: ['Statuts en temps réel', 'Rapport & signature client', 'Historique par appareil'],
            },
            {
              name: 'Application mobile',
              href: '/application-mobile',
              icon: 'fa-solid fa-mobile-screen-button',
              badge: 'iOS & Android',
              description: 'Votre équipe a tout ce qu\'il faut sur son téléphone — même sans connexion. Interventions, clients, documents, GPS.',
              pills: ['Mode offline complet', 'GPS & itinéraire', 'Signature tablette'],
            },
            {
              name: 'Suivi des chantiers',
              href: '/chantier',
              icon: 'fa-solid fa-map-location-dot',
              badge: null,
              description: 'Dossier vivant du chantier : carte, interlocuteurs, interventions, documents, facturation et rentabilité en un seul endroit.',
              pills: ['Vue carte', 'Propriétaire, régie, locataire', 'Rentabilité par chantier'],
              highlight: true,
            },
          ]
        },
        {
          id: 'planning',
          icon: 'fa-solid fa-calendar-check',
          color: 'blue',
          title: 'Planning & organisation',
          description: 'Organisez vos semaines sans Excel ni WhatsApp. Le bon technicien, au bon endroit, au bon moment.',
          features: [
            {
              name: 'Planning & tournées',
              href: '/planification',
              icon: 'fa-solid fa-calendar-check',
              badge: null,
              description: 'Planning visuel par technicien, gestion par demi-journée, couleur par personne, détection de conflits, mise à jour mobile en temps réel.',
              pills: ['Couleur par technicien', 'Demi-journée', 'Conflits détectés', 'Rappels SMS'],
            },
            {
              name: 'Prise de RDV en ligne',
              href: '/calendrier-reservation',
              icon: 'fa-solid fa-calendar-plus',
              badge: 'Nouveau',
              description: 'Vos clients réservent eux-mêmes leur créneau depuis votre site. Vous planifiez en 1 clic, sans rien ressaisir.',
              pills: ['Widget intégrable', '100% personnalisable', 'Confirmation auto'],
              highlight: true,
            },
            {
              name: 'Gestion du stock',
              href: '/stock',
              icon: 'fa-solid fa-boxes-stacked',
              badge: null,
              description: 'Stock dépôt et stock camion en temps réel. Alertes de seuil, inventaires simplifiés, préparation de chantier en amont.',
              pills: ['Stock camion temps réel', 'Alertes de seuil', 'Inventaires'],
            },
          ]
        },
        {
          id: 'facturation',
          icon: 'fa-solid fa-file-invoice-dollar',
          color: 'green',
          title: 'Facturation',
          description: 'Un moteur de facturation professionnel et certifié, inclus sans supplément. Du devis au paiement encaissé.',
          features: [
            {
              name: 'Facturation',
              href: '/facturation',
              icon: 'fa-solid fa-receipt',
              badge: 'Inclus',
              highlight: true,
              description: 'Devis en 3 min depuis votre catalogue, conversion en facture en 1 clic, multi-taux TVA, acomptes automatiques, relances, tampon PAYÉ.',
              pills: ['Multi-taux TVA', 'Acompte & solde auto', 'Relances', 'Certifié anti-fraude'],
            },
          ]
        },
        {
          id: 'conformite',
          icon: 'fa-solid fa-file-shield',
          color: 'green',
          title: 'Conformité réglementaire',
          description: 'Anti-fraude TVA, CERFA fluides, bouteilles tracées, détecteurs étalonnés — Termoflow absorbe vos obligations légales à votre place.',
          features: [
            {
              name: 'Certification anti-fraude TVA',
              href: '/facturation',
              icon: 'fa-solid fa-lock',
              badge: '2026 ready',
              highlight: true,
              description: 'SHA-256, numérotation séquentielle, audit trail, archivage 6 ans, mentions légales automatiques, e-invoicing 2026 prêt.',
              pills: ['Scellement SHA-256', 'Audit trail complet', 'e-invoicing 2026'],
            },
            {
              name: 'CERFA & fluides frigorigènes',
              href: '/cerfa',
              icon: 'fa-solid fa-file-shield',
              badge: 'F-Gaz',
              description: 'Chaque gramme de fluide manipulé génère le CERFA et met à jour le registre automatiquement. Zéro ressaisie, zéro oubli.',
              pills: ['CERFA auto-générés', 'Registre fluides', 'Archivage 5 ans'],
            },
            {
              name: 'Bouteilles & traçabilité',
              href: '/bouteilles',
              icon: 'fa-solid fa-bottle-droplet',
              badge: null,
              description: 'Suivez chaque bouteille par référence — camion, dépôt, chantier, recyclage. Stock en temps réel, mouvements tracés.',
              pills: ['Suivi par référence', 'Localisation temps réel', 'Conformité F-Gaz'],
            },
            {
              name: 'Détecteurs de gaz',
              href: '/detecteurs',
              icon: 'fa-solid fa-triangle-exclamation',
              badge: null,
              description: 'Termoflow suit les dates d\'étalonnage de vos détecteurs et vous alerte avant expiration. Ne jamais intervenir hors conformité.',
              pills: ['Alertes étalonnage', 'Historique contrôles'],
            },
          ]
        },
        {
          id: 'clients',
          icon: 'fa-solid fa-users',
          color: 'purple',
          title: 'Clients & croissance',
          description: 'Fidélisez, automatisez, développez — chaque client devient une source de revenus récurrents sans effort supplémentaire.',
          features: [
            {
              name: 'Gestion des clients',
              href: '/gestion-client',
              icon: 'fa-solid fa-user-tie',
              badge: null,
              description: 'Fiche client complète, historique des interventions, appareils installés, contacts multiples (propriétaire, régie, locataire).',
              pills: ['Historique complet', 'Multi-contacts', 'Appareils liés'],
            },
            {
              name: 'Contrats d\'entretien',
              href: '/contrats-entretien',
              icon: 'fa-solid fa-file-contract',
              badge: null,
              description: 'Parcs sous contrat avec relances automatiques, planification des visites et suivi de la rentabilité par contrat.',
              pills: ['Relances auto', 'Rentabilité par contrat', 'Parcs multi-appareils'],
            },
            {
              name: 'Portail client',
              href: '/portail-client',
              icon: 'fa-solid fa-globe',
              badge: null,
              description: 'Vos clients accèdent à leurs CERFA, photos, historique et prise de RDV en ligne — sans appeler votre bureau.',
              pills: ['CERFA en autonomie', 'RDV en ligne', 'Historique photos'],
            },
            {
              name: 'Communication automatisée',
              href: '/communication-automatisee',
              icon: 'fa-solid fa-paper-plane',
              badge: null,
              description: 'SMS avant intervention, email post-rapport, invitation avis Google — tout se déclenche sans action manuelle.',
              pills: ['SMS rappel RDV', 'Email post-intervention', 'Avis Google auto'],
            },
          ]
        },
        {
          id: 'pilotage',
          icon: 'fa-solid fa-chart-line',
          color: 'blue',
          title: 'Pilotage & paramétrage',
          description: 'Les chiffres pour décider et les paramètres pour que Termoflow colle exactement à votre façon de travailler.',
          features: [
            {
              name: 'Statistiques & pilotage',
              href: '/statistiques',
              icon: 'fa-solid fa-chart-bar',
              badge: null,
              description: 'CA du mois, marge par technicien, taux de rentabilité par chantier, taux de transformation devis — pilotez avec des données.',
              pills: ['KPIs temps réel', 'Marge par technicien', 'Rentabilité chantiers'],
            },
            {
              name: 'Paramétrage métier',
              href: '/parametrage',
              icon: 'fa-solid fa-sliders',
              badge: 'Nouveau',
              highlight: true,
              description: 'Types d\'appareils, interventions, travaux, points de contrôle, tarifs de contrat — configurez Termoflow à votre image une bonne fois.',
              pills: ['Types d\'appareils', 'Points de contrôle', 'Tarifs par appareil'],
            },
            {
              name: 'Exports & intégrations',
              href: '/exportation-donnees',
              icon: 'fa-solid fa-plug',
              badge: null,
              description: 'Export comptable en 1 clic, journal de ventes, données TVA structurées, connexion à votre logiciel existant via API REST.',
              pills: ['Export CSV / Excel', 'Journal de ventes', 'API REST'],
            },
          ]
        },
        {
          id: 'accompagnement',
          icon: 'fa-solid fa-headset',
          color: 'orange',
          title: 'Accompagnement',
          description: 'On ne vous livre pas un logiciel et on disparaît. On est là à chaque étape — onboarding, utilisation quotidienne, questions.',
          features: [
            {
              name: 'Support humain',
              href: '/support',
              icon: 'fa-solid fa-comment-dots',
              badge: '< 4h',
              highlight: true,
              description: 'Chat intégré avec réponse en moins de 4 heures, email, téléphone sur demande. Une vraie personne, qui connaît le secteur CVC.',
              pills: ['Chat intégré', 'Pas de bot', 'Réponse < 4h'],
            },
            {
              name: 'Onboarding accompagné',
              href: '/support',
              icon: 'fa-solid fa-rocket',
              badge: null,
              description: 'Session de configuration en visio à votre souscription, import de vos données existantes, suivi à 30 jours.',
              pills: ['Visio de config', 'Import données', 'Suivi J+30'],
            },
          ]
        },
      ]
    }
  }
}
</script>

<style scoped>
.gradient-text { background: linear-gradient(to right, #f97316, #ef4444); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }

.hero-section { padding: 10rem 1rem 5rem; background: radial-gradient(ellipse at top right,#fff7ed,transparent 45%), radial-gradient(ellipse at bottom left,#fef2f2,transparent 45%); }
.hero-badge { display:inline-block; padding:.5rem 1.25rem; background:white; border:1px solid #e5e7eb; border-radius:9999px; font-size:.75rem; font-weight:800; color:#f97316; text-transform:uppercase; letter-spacing:.08em; margin-bottom:1.75rem; }
.hero-title { font-size:clamp(2.5rem,5vw,3.75rem); font-weight:900; color:#111827; line-height:1.15; margin-bottom:1.25rem; }
.hero-sub { font-size:1.15rem; color:#6b7280; max-width:680px; margin:0 auto 3rem; line-height:1.7; }
.hero-proof { display:inline-flex; align-items:center; background:white; border:1px solid #e5e7eb; border-radius:9999px; padding:.5rem 2rem; flex-wrap:wrap; justify-content:center; }
.hp-item { display:flex; align-items:center; gap:.6rem; padding:.35rem 1.25rem; }
.hp-val { font-size:1.15rem; font-weight:900; color:#f97316; }
.hp-lab { font-size:.72rem; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }
.hp-sep { width:1px; height:1.5rem; background:#e5e7eb; }

.groups-section { padding:4rem 0 5rem; }
.group-block { margin-bottom:4.5rem; scroll-margin-top:5rem; }
.group-header { display:flex; align-items:flex-start; gap:1.25rem; margin-bottom:1.75rem; padding-bottom:1.5rem; border-bottom:1px solid #f1f5f9; }
.group-icon-wrap { width:3rem; height:3rem; border-radius:1rem; display:flex; align-items:center; justify-content:center; font-size:1.2rem; flex-shrink:0; margin-top:.1rem; }
.group-icon-wrap.orange { background:#fff7ed; color:#f97316; }
.group-icon-wrap.blue   { background:#eff6ff; color:#2563eb; }
.group-icon-wrap.green  { background:#f0fdf4; color:#16a34a; }
.group-icon-wrap.purple { background:#f5f3ff; color:#7c3aed; }
.group-title { font-size:1.5rem; font-weight:900; color:#111827; margin-bottom:.3rem; }
.group-desc { font-size:.95rem; color:#6b7280; line-height:1.6; }

.feat-grid { display:grid; gap:1.25rem; }
.feat-grid.cols-1 { grid-template-columns:1fr; max-width:420px; }
.feat-grid.cols-2 { grid-template-columns:repeat(2,1fr); }
.feat-grid.cols-3 { grid-template-columns:repeat(3,1fr); }
.feat-grid.cols-4 { grid-template-columns:repeat(4,1fr); }
@media(max-width:900px) { .feat-grid.cols-3,.feat-grid.cols-4 { grid-template-columns:repeat(2,1fr); } }
@media(max-width:560px) { .feat-grid.cols-2,.feat-grid.cols-3,.feat-grid.cols-4 { grid-template-columns:1fr; } }

.feat-card { display:flex; flex-direction:column; background:white; border:1px solid #e5e7eb; border-radius:1.25rem; padding:1.75rem; text-decoration:none; transition:border-color .2s,box-shadow .2s,transform .2s; }
.feat-card:hover { border-color:#d1d5db; box-shadow:0 8px 30px rgba(0,0,0,.07); transform:translateY(-3px); }
.feat-highlight { border-color:#fed7aa; background:linear-gradient(135deg,#fff,#fff7ed); }
.feat-highlight:hover { border-color:#f97316; }

.fc-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.1rem; }
.fc-icon { width:2.5rem; height:2.5rem; border-radius:.75rem; display:flex; align-items:center; justify-content:center; font-size:1rem; flex-shrink:0; }
.fc-icon.orange { background:#fff7ed; color:#f97316; }
.fc-icon.blue   { background:#eff6ff; color:#2563eb; }
.fc-icon.green  { background:#f0fdf4; color:#16a34a; }
.fc-icon.purple { background:#f5f3ff; color:#7c3aed; }

.fc-badge { font-size:.65rem; font-weight:800; padding:.25rem .65rem; border-radius:9999px; text-transform:uppercase; letter-spacing:.04em; white-space:nowrap; }
.badge-green { background:#f0fdf4; color:#166534; }
.badge-orange { background:#fff7ed; color:#c2410c; }
.badge-blue   { background:#eff6ff; color:#1d4ed8; }
.badge-gray   { background:#f1f5f9; color:#475569; }

.fc-name { font-size:1.05rem; font-weight:900; color:#111827; margin-bottom:.5rem; }
.fc-desc { font-size:.875rem; color:#6b7280; line-height:1.65; flex:1; margin-bottom:1.1rem; }
.fc-pills { display:flex; flex-wrap:wrap; gap:.4rem; padding:0; margin:0 0 1.25rem; list-style:none; }
.fc-pills li { font-size:.72rem; font-weight:700; color:#374151; background:#f9fafb; border:1px solid #e5e7eb; padding:.2rem .65rem; border-radius:9999px; }
.fc-link { display:inline-flex; align-items:center; gap:.4rem; font-size:.8rem; font-weight:800; color:#f97316; margin-top:auto; }
.fc-link i { font-size:.7rem; transition:transform .2s; }
.feat-card:hover .fc-link i { transform:translateX(3px); }

/* Compare section */
.compare-section { background:#0f172a; padding:6rem 0; }
.compare-grid { display:grid; grid-template-columns:1fr auto 1fr; gap:2rem; align-items:start; }
@media(max-width:768px) { .compare-grid { grid-template-columns:1fr; } .compare-arrow { transform:rotate(90deg); } }
.compare-col { border-radius:1.5rem; overflow:hidden; border:1px solid #1e293b; }
.compare-col-head { padding:1rem 1.5rem; font-size:.75rem; font-weight:800; text-transform:uppercase; letter-spacing:.07em; }
.before-head { background:#1e293b; color:#64748b; }
.after-head  { background:#f97316; color:white; }
.compare-item { display:flex; align-items:center; gap:.85rem; padding:.8rem 1.5rem; border-bottom:1px solid #1e293b; background:#0f172a; }
.compare-item i { color:#334155; font-size:.9rem; flex-shrink:0; }
.compare-item b { display:block; font-size:.8rem; font-weight:800; color:#94a3b8; }
.compare-item small { font-size:.68rem; color:#475569; font-weight:600; }
.compare-item div { flex:1; }
.before-cost { font-size:.75rem; font-weight:800; color:#ef4444; white-space:nowrap; }
.before-total { display:flex; justify-content:space-between; padding:1rem 1.5rem; background:#1e293b; font-size:.82rem; font-weight:700; color:#64748b; }
.bt-val { color:#ef4444; font-weight:900; }
.compare-arrow { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.5rem; padding-top:3rem; color:#f97316; }
.compare-arrow i { font-size:1.5rem; }
.compare-arrow span { font-size:.68rem; font-weight:800; color:#475569; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
.after-col { border-color:rgba(249,115,22,.3); }
.after-logo { display:flex; align-items:center; gap:.75rem; padding:1.25rem 1.5rem; font-size:1.2rem; font-weight:900; color:white; background:#0f172a; border-bottom:1px solid #1e293b; }
.after-logo i { color:#f97316; font-size:1.4rem; }
.after-checks { background:#0f172a; padding:.75rem 1.5rem; }
.after-check { display:flex; align-items:center; gap:.6rem; padding:.5rem 0; font-size:.8rem; font-weight:700; color:#94a3b8; border-bottom:1px solid #1e293b; }
.after-check i { color:#22c55e; font-size:.65rem; flex-shrink:0; }
.after-total { background:rgba(249,115,22,.1); border-top:1px solid rgba(249,115,22,.3); padding:1rem 1.5rem; display:flex; flex-direction:column; gap:.15rem; }
.after-total span { font-size:.88rem; font-weight:800; color:#f97316; }
.at-sub { font-size:.72rem; font-weight:700; color:#475569; }

.cta-section { padding:7rem 0; background:white; }
.cta-buttons { display:flex; flex-wrap:wrap; justify-content:center; gap:1rem; }
.btn-primary-large { background:linear-gradient(to right,#f97316,#ef4444); color:white; padding:1.2rem 3rem; border-radius:9999px; font-weight:800; border:none; font-size:1.05rem; cursor:pointer; }
.btn-outline-large { border:2px solid #e5e7eb; padding:1.2rem 3rem; border-radius:9999px; font-weight:800; color:#1f2937; text-decoration:none; display:inline-block; font-size:1.05rem; }
html { scroll-behavior: smooth; }
</style>